import React, { useState } from 'react';
import styled from '@emotion/styled';

import { ComponentStyle, ProductComponentProps } from '../../types/types';

import { CtaHeader } from './CtaHeader';
import { CtaBackgroundPattern } from './CtaBackgroundPattern';
import { CtaButton } from './CtaButton';

import { StyleLibrary } from '@vm/omega-shared-component-webapp';
const { mqBreakpoints, pxToRem } = StyleLibrary;

import { productComponentContainerPaddingStyle, productComponentContainerBottomPaddingStyle } from '../shared/styles';
import { CtaTp } from './CtaTp/CtaTp';
import { extractPaletteColors } from '../helpers';
import { CtaModal } from './CtaModal';
import { PrimaryButton } from '../shared/Buttons';

const CtaContainer = styled.div({
  overflow: 'hidden',
  position: 'relative',
  // z-index is 1 here to ensure the pattern is displayed
  zIndex: 1,

  '&.A': {
    backgroundColor: 'var(--color-dark)',
    color: 'var(--color-white)',

    h1: {
      color: 'var(--color-secondary)',
    },
  },

  '&.B': {
    backgroundColor: 'var(--color-secondary-2)',
    color: 'var(--color-white)',

    h1: {
      color: 'var(--color-secondary)',
    },
  },

  '&.C': {
    backgroundColor: 'var(--color-light)',
    color: 'var(--color-body)',

    h1: {
      color: 'var(--color-primary)',
    },
  },
});

const CtaContentContainer = styled.div(productComponentContainerPaddingStyle, productComponentContainerBottomPaddingStyle, {
  paddingBottom: pxToRem(128),

  [mqBreakpoints.tablet]: {
    paddingRight: pxToRem(280),
  },

  [mqBreakpoints.desktop]: {
    paddingTop: pxToRem(96),
    paddingRight: pxToRem(520),
  },
});

type Props = ProductComponentProps<{ onCtaClick: (cta: string) => void }>;

export const CTA: React.FC<Props> = props => {
  const { title, points, description, cta, style, palette, logo, image } = props.productComponent;

  const paletteColors = extractPaletteColors(palette);

  const onCtaClick = () => {
    props.onCtaClick(cta.cta);
  };

  const componentIdx = props.index ?? 0;

  const hasModal = cta?.cta && cta.cta.includes('cta-modal');
  const [isModalOpen, setIsModalOpen] = useState(false);

  switch (style) {
    case ComponentStyle.D:
    case ComponentStyle.E:
    case ComponentStyle.F:
      return (
        <CtaTp
          index={componentIdx}
          data-testid='mp-product-cta-tp'
          title={title}
          points={points}
          description={description}
          cta={cta}
          onCtaClick={onCtaClick}
          paletteBackgroundColor={paletteColors.paletteBackgroundColor}
          paletteTextColor={paletteColors.paletteTextColor}
          logo={logo}
          style={style}
          image={image}
        ></CtaTp>
      );
    default:
      return (
        <CtaContainer id={`mp-product-cta-${componentIdx}`} className={style} data-testid='mp-product-cta'>
          <CtaBackgroundPattern style={style} />
          <CtaContentContainer>
            <CtaHeader title={title} strapline={description} />
            {cta && !hasModal && <CtaButton title={cta.buttonText} onClick={onCtaClick} />}
            {cta && hasModal && (
              <>
                <PrimaryButton data-testid='mp-product-cta-button' onClick={() => setIsModalOpen(true)}>
                  {cta.buttonText}
                </PrimaryButton>
                <CtaModal modalContent={cta.cta} onCtaClick={onCtaClick} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
              </>
            )}
          </CtaContentContainer>
        </CtaContainer>
      );
  }
};
